import { FormEvent, useEffect, useRef, useState } from 'react'
import {
  ArrowLeft, Check, Eye, EyeOff, Gift, Loader2, LockKeyhole, Mail,
  MapPin, ShieldAlert, Trash2, Users, Shield, User, ExternalLink, Image as ImageIcon,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useLanguage } from '@/context/LanguageContext'
import GiftLoader from '@/components/GiftLoader'
import HomepageGalleryManager from '@/components/profile/HomepageGalleryManager'

type Tab = 'security' | 'address' | 'users' | 'gallery'

interface UserRow {
  id: string
  display_name: string | null
  username: string | null
  avatar_url: string | null
  created_at: string
  email: string | null
}

const i18n = {
  it: {
    backToDashboard: 'Dashboard',
    accountSettings: 'Impostazioni account',
    profile: 'Profilo',
    profileSubtitle: 'Gestisci le informazioni principali e le credenziali del tuo account.',
    tabSecurity: 'Sicurezza',
    tabAddress: 'Indirizzo',
    tabUsers: 'Utenti',
    tabGallery: 'Gallery Homepage',
    emailTitle: 'Email',
    emailSubtitle: "Aggiorna l'indirizzo associato al tuo account.",
    emailLabel: 'Indirizzo email',
    emailUpdateBtn: 'Aggiorna email',
    emailSuccess: 'Controlla la nuova casella email per confermare la modifica.',
    passwordTitle: 'Password',
    passwordSubtitle: 'Scegli una nuova password di almeno 6 caratteri.',
    newPasswordLabel: 'Nuova password',
    confirmPasswordLabel: 'Conferma password',
    passwordUpdateBtn: 'Aggiorna password',
    passwordSuccess: 'Password aggiornata correttamente.',
    passwordTooShort: 'La password deve contenere almeno 6 caratteri.',
    passwordMismatch: 'Le password non coincidono.',
    showPassword: 'Mostra password',
    hidePassword: 'Nascondi password',
    deleteAccountTitle: 'Elimina account',
    deleteAccountDesc: "L'eliminazione dell'account è permanente e irreversibile. Tutti i tuoi dati — wishlist, categorie, oggetti, indirizzo e profilo — verranno cancellati immediatamente. Questa azione non può essere annullata.",
    deleteMyAccount: 'Elimina il mio account',
    deleteConfirmPrompt: 'Per confermare, digita ELIMINA nel campo qui sotto e premi il pulsante. Verrai disconnesso e il tuo account verrà cancellato definitivamente.',
    deleteConfirmLabel: 'Digita ELIMINA per confermare',
    deleteConfirmPlaceholder: 'ELIMINA',
    deletePermanentBtn: 'Elimina definitivamente',
    cancel: 'Annulla',
    deleteError: "Errore durante l'eliminazione dell'account. Riprova più tardi.",
    addressTitle: 'Indirizzo di spedizione',
    addressDesc: "Inserisci un indirizzo dove preferisci ricevere i tuoi regali: casa, ufficio, o qualsiasi altro posto. Chi vorrà farti una sorpresa potrà recapitartelo direttamente lì. Se preferisci non indicarlo, nessun problema: chi ti vuole bene potrà sempre acquistare il regalo e consegnartelo di persona, all'evento, o quando vi incontrate.",
    addressWarning: "Attenzione: l'indirizzo che inserisci sarà visibile pubblicamente a chiunque visualizzi la tua wishlist, inclusi utenti non registrati. Assicurati di inserire solo un indirizzo che sei a tuo agio nel condividere pubblicamente.",
    recipientLabel: 'Intestatario (Nome e Cognome)',
    recipientPlaceholder: 'Mario Rossi',
    addressLabel: 'Indirizzo di spedizione',
    addressPlaceholder: 'Via, civico, città, CAP, provincia...',
    addressConsent: 'Ho letto e compreso che il mio indirizzo sarà visibile pubblicamente. Acconsento al trattamento di questo dato personale ai sensi del GDPR e della ',
    addressConsentLink: 'Privacy Policy',
    addressConsentAfter: ' di Make me a Gift.',
    addressConsentError: "Devi spuntare la casella di consenso per salvare l'indirizzo.",
    recipientRequiredError: "Se indichi l'indirizzo di spedizione, l'intestatario (Nome e Cognome) è obbligatorio.",
    addressSaveBtn: 'Salva indirizzo',
    addressSuccess: 'Indirizzo di spedizione salvato correttamente.',
    usersTitle: 'Utenti registrati',
    usersSubtitle: 'Lista con link alle wishlist pubbliche.',
    userCountSingular: 'utente',
    userCountPlural: 'utenti',
    usersError: 'Impossibile caricare la lista degli utenti.',
    noUsers: 'Nessun utente registrato.',
    noName: 'Senza nome',
    noUsername: 'Nessuno username',
    incompleteSignup: 'Iscrizione incompleta',
    wishlistLink: 'Wishlist',
  },
  en: {
    backToDashboard: 'Dashboard',
    accountSettings: 'Account settings',
    profile: 'Profile',
    profileSubtitle: 'Manage your main information and account credentials.',
    tabSecurity: 'Security',
    tabAddress: 'Address',
    tabUsers: 'Users',
    tabGallery: 'Gallery Homepage',
    emailTitle: 'Email',
    emailSubtitle: 'Update the email address associated with your account.',
    emailLabel: 'Email address',
    emailUpdateBtn: 'Update email',
    emailSuccess: 'Check your new email inbox to confirm the change.',
    passwordTitle: 'Password',
    passwordSubtitle: 'Choose a new password of at least 6 characters.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm password',
    passwordUpdateBtn: 'Update password',
    passwordSuccess: 'Password updated successfully.',
    passwordTooShort: 'Password must be at least 6 characters long.',
    passwordMismatch: 'Passwords do not match.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    deleteAccountTitle: 'Delete account',
    deleteAccountDesc: 'Account deletion is permanent and irreversible. All your data — wishlists, categories, items, address and profile — will be deleted immediately. This action cannot be undone.',
    deleteMyAccount: 'Delete my account',
    deleteConfirmPrompt: 'To confirm, type DELETE in the field below and press the button. You will be logged out and your account will be permanently deleted.',
    deleteConfirmLabel: 'Type DELETE to confirm',
    deleteConfirmPlaceholder: 'DELETE',
    deletePermanentBtn: 'Delete permanently',
    cancel: 'Cancel',
    deleteError: 'Error during account deletion. Please try again later.',
    addressTitle: 'Shipping address',
    addressDesc: 'Enter an address where you prefer to receive your gifts: home, office, or anywhere else. Anyone who wants to surprise you can deliver it directly there. If you prefer not to provide it, no problem: your loved ones can always purchase the gift and deliver it in person, at the event, or when you meet.',
    addressWarning: 'Warning: the address you enter will be publicly visible to anyone viewing your wishlist, including unregistered users. Make sure you only enter an address you are comfortable sharing publicly.',
    recipientLabel: 'Recipient (Full name)',
    recipientPlaceholder: 'John Smith',
    addressLabel: 'Shipping address',
    addressPlaceholder: 'Street, number, city, zip code, province...',
    addressConsent: 'I have read and understood that my address will be publicly visible. I consent to the processing of this personal data under GDPR and the ',
    addressConsentLink: 'Privacy Policy',
    addressConsentAfter: ' of Make me a Gift.',
    addressConsentError: 'You must check the consent box to save the address.',
    recipientRequiredError: 'If you provide a shipping address, the recipient (Full name) is required.',
    addressSaveBtn: 'Save address',
    addressSuccess: 'Shipping address saved successfully.',
    usersTitle: 'Registered users',
    usersSubtitle: 'List with links to public wishlists.',
    userCountSingular: 'user',
    userCountPlural: 'users',
    usersError: 'Unable to load the user list.',
    noUsers: 'No registered users.',
    noName: 'No name',
    noUsername: 'No username',
    incompleteSignup: 'Incomplete signup',
    wishlistLink: 'Wishlist',
  },
}

export default function ProfilePage() {
  const { user, loading } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    if (!user) { setIsAdmin(false); return }
    supabase
      .rpc('has_role', { _user_id: user.id, _role: 'admin' })
      .then(({ data }) => setIsAdmin(!!data))
  }, [user?.id])

  return <ProfilePageContent />
}

function ProfilePageContent() {
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const { language } = useLanguage()
  const c = i18n[language]
  const [activeTab, setActiveTab] = useState<Tab>('security')
  const [isAdmin, setIsAdmin] = useState(false)

  // Security state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [savingEmail, setSavingEmail] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [emailMessage, setEmailMessage] = useState<string | null>(null)
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Address state
  const [shippingRecipient, setShippingRecipient] = useState('')
  const [shippingAddress, setShippingAddress] = useState('')
  const [shippingConsent, setShippingConsent] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)
  const [removingAddress, setRemovingAddress] = useState(false)
  const [addressMessage, setAddressMessage] = useState<string | null>(null)
  const [addressError, setAddressError] = useState<string | null>(null)

  // Users state
  const [users, setUsers] = useState<UserRow[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [usersError, setUsersError] = useState<string | null>(null)

  // Tab scroll fade state
  const tabScrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const deleteKeyword = language === 'it' ? 'ELIMINA' : 'DELETE'

  useEffect(() => {
    const el = tabScrollRef.current
    if (!el) return
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 4)
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [isAdmin])

  useEffect(() => {
    if (!loading && !user) navigate('/login')
    if (user?.email) setEmail(user.email)
    if (user) {
      supabase
        .from('profiles')
        .select('shipping_recipient, shipping_address, shipping_address_consent')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          if (data) {
            setShippingRecipient(data.shipping_recipient ?? '')
            setShippingAddress(data.shipping_address ?? '')
            setShippingConsent(!!data.shipping_address_consent)
          }
        })
      supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .then(({ data }) => {
          if (data && data.some((r: { role: string }) => r.role === 'admin')) {
            setIsAdmin(true)
          }
        })
    }
  }, [loading, user, navigate])

  useEffect(() => {
    if (activeTab === 'users' && isAdmin) {
      setLoadingUsers(true)
      setUsersError(null)
      supabase
        .rpc('list_users_with_emails')
        .then(({ data, error }) => {
          setLoadingUsers(false)
          if (error) {
            setUsersError(c.usersError)
            return
          }
          setUsers((data as UserRow[]) ?? [])
        })
    }
  }, [activeTab, isAdmin, c.usersError])

  const updateEmail = async (event: FormEvent) => {
    event.preventDefault()
    if (!user || email.trim() === user.email) return
    setSavingEmail(true)
    setEmailError(null)
    setEmailMessage(null)
    const { error } = await supabase.auth.updateUser({ email: email.trim() })
    setSavingEmail(false)
    if (error) {
      setEmailError(error.message)
      return
    }
    setEmailMessage(c.emailSuccess)
  }

  const updatePassword = async (event: FormEvent) => {
    event.preventDefault()
    setPasswordError(null)
    setPasswordMessage(null)
    if (password.length < 6) {
      setPasswordError(c.passwordTooShort)
      return
    }
    if (password !== confirmPassword) {
      setPasswordError(c.passwordMismatch)
      return
    }
    setSavingPassword(true)
    const { error } = await supabase.auth.updateUser({ password })
    setSavingPassword(false)
    if (error) {
      setPasswordError(error.message)
      return
    }
    setPassword('')
    setConfirmPassword('')
    setPasswordMessage(c.passwordSuccess)
  }

  const saveShippingAddress = async (event: FormEvent) => {
    event.preventDefault()
    if (!user) return
    setAddressError(null)
    setAddressMessage(null)
    if (shippingAddress.trim() && !shippingRecipient.trim()) {
      setAddressError(c.recipientRequiredError)
      return
    }
    if (shippingAddress.trim() && !shippingConsent) {
      setAddressError(c.addressConsentError)
      return
    }
    setSavingAddress(true)
    const { error } = await supabase
      .from('profiles')
      .update({
        shipping_recipient: shippingRecipient.trim() || null,
        shipping_address: shippingAddress.trim() || null,
        shipping_address_consent: shippingAddress.trim() ? shippingConsent : false,
      })
      .eq('id', user.id)
    setSavingAddress(false)
    if (error) {
      setAddressError(error.message)
      return
    }
    setAddressMessage(c.addressSuccess)
  }

  const removeShippingAddress = async () => {
    if (!user || removingAddress) return
    setRemovingAddress(true)
    setAddressError(null)
    setAddressMessage(null)
    const { error } = await supabase
      .from('profiles')
      .update({
        shipping_recipient: null,
        shipping_address: null,
        shipping_address_consent: false,
      })
      .eq('id', user.id)
    setRemovingAddress(false)
    if (error) {
      setAddressError(error.message)
      return
    }
    setShippingRecipient('')
    setShippingAddress('')
    setShippingConsent(false)
    setAddressMessage(language === 'it' ? 'Indirizzo rimosso' : 'Address removed')
  }

  const deleteAccount = async () => {
    if (!user) return
    if (deleteConfirmText !== deleteKeyword) return
    setDeletingAccount(true)
    setDeleteError(null)
    const { error } = await supabase.functions.invoke('delete-account')
    setDeletingAccount(false)
    if (error) {
      setDeleteError(c.deleteError)
      return
    }
    await supabase.auth.signOut()
    navigate('/')
  }

  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <GiftLoader />
      </div>
    )
  }

  const tabs: { id: Tab; label: string; icon: typeof Shield; adminOnly?: boolean }[] = [
    { id: 'security', label: c.tabSecurity, icon: Shield },
    { id: 'address', label: c.tabAddress, icon: MapPin },
    { id: 'users', label: c.tabUsers, icon: Users, adminOnly: true },
    { id: 'gallery', label: c.tabGallery, icon: ImageIcon, adminOnly: true },
  ]

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border/30 glass-strong">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display font-semibold">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-900 text-white">
              <Gift className="h-5 w-5" />
            </span>
            Make me a Gift
          </Link>
          <Link
            to="/dashboard"
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-white px-4 text-sm font-semibold text-neutral-700 transition hover:border-neutral-400"
          >
            <ArrowLeft className="h-4 w-4" />
            {c.backToDashboard}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
        <div className="mb-7">
          <p className="text-sm font-semibold text-pink-600">{c.accountSettings}</p>
          <h1 className="mt-1 text-3xl font-extrabold sm:text-4xl">{c.profile}</h1>
          <p className="mt-2 max-w-xl text-muted-foreground">
            {c.profileSubtitle}
          </p>
        </div>

        {/* Tabs */}
        <div className="relative mb-6">
          <div
            ref={tabScrollRef}
            className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {tabs.filter((t) => !t.adminOnly || isAdmin).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex min-h-11 items-center gap-2 rounded-full px-5 text-sm font-semibold transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-neutral-900 text-white'
                    : 'border border-border bg-white text-neutral-700 hover:border-neutral-400'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
          <div
            className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background via-background/60 to-transparent transition-opacity duration-200 ${
              canScrollLeft ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <div
            className={`pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background via-background/60 to-transparent transition-opacity duration-200 ${
              canScrollRight ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>

        {/* === TAB: Security === */}
        {activeTab === 'security' && (
          <div className="space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <section className="rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                    <Mail className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold">{c.emailTitle}</h2>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{c.emailSubtitle}</p>

                <form onSubmit={updateEmail} className="mt-5 space-y-4">
                  <div>
                    <label htmlFor="profile-email" className="mb-1.5 block text-sm font-semibold">{c.emailLabel}</label>
                    <input
                      id="profile-email"
                      type="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="min-h-11 w-full rounded-xl border border-border bg-white px-3 text-sm outline-none transition focus:border-neutral-500 focus:ring-2 focus:ring-neutral-900/10"
                    />
                  </div>
                  {emailError && <p role="alert" className="text-sm text-red-600">{emailError}</p>}
                  {emailMessage && <p className="flex items-start gap-1.5 text-sm text-green-600"><Check className="mt-0.5 h-4 w-4 shrink-0" />{emailMessage}</p>}
                  <button
                    type="submit"
                    disabled={savingEmail || email.trim() === user.email}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingEmail && <Loader2 className="h-4 w-4 animate-spin" />}
                    {c.emailUpdateBtn}
                  </button>
                </form>
              </section>

              <section className="rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-pink-100 text-pink-600">
                    <LockKeyhole className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold">{c.passwordTitle}</h2>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{c.passwordSubtitle}</p>

                <form onSubmit={updatePassword} className="mt-5 space-y-4">
                  <div>
                    <label htmlFor="profile-password" className="mb-1.5 block text-sm font-semibold">{c.newPasswordLabel}</label>
                    <div className="relative">
                      <input
                        id="profile-password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="min-h-11 w-full rounded-xl border border-border bg-white px-3 pr-12 text-sm outline-none transition focus:border-neutral-500 focus:ring-2 focus:ring-neutral-900/10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        aria-label={showPassword ? c.hidePassword : c.showPassword}
                        className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="profile-confirm-password" className="mb-1.5 block text-sm font-semibold">{c.confirmPasswordLabel}</label>
                    <div className="relative">
                      <input
                        id="profile-confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        className="min-h-11 w-full rounded-xl border border-border bg-white px-3 pr-12 text-sm outline-none transition focus:border-neutral-500 focus:ring-2 focus:ring-neutral-900/10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        aria-label={showPassword ? c.hidePassword : c.showPassword}
                        className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {passwordError && <p role="alert" className="text-sm text-red-600">{passwordError}</p>}
                  {passwordMessage && <p className="flex items-center gap-1.5 text-sm text-green-600"><Check className="h-4 w-4" />{passwordMessage}</p>}
                  <button
                    type="submit"
                    disabled={savingPassword}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:opacity-50"
                  >
                    {savingPassword && <Loader2 className="h-4 w-4 animate-spin" />}
                    {c.passwordUpdateBtn}
                  </button>
                </form>
              </section>
            </div>

            {!isAdmin && (
            <section className="rounded-2xl border border-red-200 bg-red-50/50 p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                  <Trash2 className="h-5 w-5" />
                </span>
                <h2 className="text-xl font-bold text-red-700">{c.deleteAccountTitle}</h2>
              </div>
              <p className="mt-2 text-sm text-red-600/80 leading-relaxed">
                {c.deleteAccountDesc}
              </p>

              {!showDeleteConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-red-300 bg-white px-5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {c.deleteMyAccount}
                </button>
              ) : (
                <div className="mt-5 space-y-4">
                  <div className="flex items-start gap-2.5 rounded-xl bg-red-100 p-3.5 text-sm text-red-700">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                      {c.deleteConfirmPrompt}
                    </p>
                  </div>
                  <div>
                    <label htmlFor="delete-confirm" className="mb-1.5 block text-sm font-semibold text-red-700">{c.deleteConfirmLabel}</label>
                    <input
                      id="delete-confirm"
                      type="text"
                      value={deleteConfirmText}
                      onChange={(event) => setDeleteConfirmText(event.target.value)}
                      placeholder={c.deleteConfirmPlaceholder}
                      className="min-h-11 w-full rounded-xl border border-red-300 bg-white px-3 text-sm outline-none transition focus:border-red-500 focus:ring-2 focus:ring-red-900/10"
                    />
                  </div>
                  {deleteError && <p role="alert" className="text-sm text-red-600">{deleteError}</p>}
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={deleteAccount}
                      disabled={deletingAccount || deleteConfirmText !== deleteKeyword}
                      className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-red-600 px-5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {deletingAccount && <Loader2 className="h-4 w-4 animate-spin" />}
                      {c.deletePermanentBtn}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(null) }}
                      disabled={deletingAccount}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white px-5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-50"
                    >
                      {c.cancel}
                    </button>
                  </div>
                </div>
              )}
            </section>
            )}
          </div>
        )}

        {/* === TAB: Address === */}
        {activeTab === 'address' && (
          <section className="rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <MapPin className="h-5 w-5" />
              </span>
              <h2 className="text-xl font-bold">{c.addressTitle}</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {c.addressDesc}
            </p>

            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-amber-50 p-3.5 text-sm text-amber-800">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                <strong>{language === 'it' ? 'Attenzione:' : 'Warning:'}</strong> {c.addressWarning}
              </p>
            </div>

            <form onSubmit={saveShippingAddress} className="mt-5 space-y-4">
              <div>
                <label htmlFor="shipping-recipient" className="mb-1.5 block text-sm font-semibold">{c.recipientLabel}</label>
                <input
                  id="shipping-recipient"
                  type="text"
                  value={shippingRecipient}
                  onChange={(event) => setShippingRecipient(event.target.value)}
                  placeholder={c.recipientPlaceholder}
                  className="min-h-11 w-full rounded-xl border border-border bg-white px-3 text-sm outline-none transition focus:border-neutral-500 focus:ring-2 focus:ring-neutral-900/10"
                />
              </div>

              <div>
                <label htmlFor="shipping-address" className="mb-1.5 block text-sm font-semibold">{c.addressLabel}</label>
                <textarea
                  id="shipping-address"
                  rows={3}
                  value={shippingAddress}
                  onChange={(event) => setShippingAddress(event.target.value)}
                  placeholder={c.addressPlaceholder}
                  className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-neutral-500 focus:ring-2 focus:ring-neutral-900/10"
                />
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shippingConsent}
                  onChange={(event) => setShippingConsent(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-neutral-900"
                />
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {c.addressConsent}
                  <Link to="/privacy" className="text-primary underline underline-offset-2 hover:text-primary/80">{c.addressConsentLink}</Link>
                  {' '}{c.addressConsentAfter}
                </span>
              </label>

              {addressError && <p role="alert" className="text-sm text-red-600">{addressError}</p>}
              {addressMessage && <p className="flex items-center gap-1.5 text-sm text-green-600"><Check className="h-4 w-4" />{addressMessage}</p>}
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="submit"
                  disabled={savingAddress || (shippingAddress.trim() !== '' && !shippingConsent)}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  {savingAddress && <Loader2 className="h-4 w-4 animate-spin" />}
                  {c.addressSaveBtn}
                </button>
                <button
                  type="button"
                  onClick={removeShippingAddress}
                  disabled={removingAddress || (shippingAddress.trim() === '' && shippingRecipient.trim() === '')}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full border border-red-300 bg-white px-5 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  {removingAddress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  {language === 'it' ? 'Rimuovi indirizzo' : 'Remove address'}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* === TAB: Users (admin only) === */}
        {activeTab === 'users' && isAdmin && (
          <section className="rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                  <Users className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-xl font-bold">{c.usersTitle}</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {c.usersSubtitle}
                  </p>
                </div>
              </div>
              {!loadingUsers && !usersError && (
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-violet-100 px-3 py-1.5 text-sm font-bold text-violet-700">
                  {users.length} {users.length === 1 ? c.userCountSingular : c.userCountPlural}
                </span>
              )}
            </div>

            {loadingUsers ? (
              <div className="mt-6 flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : usersError ? (
              <p className="mt-6 text-sm text-red-600">{usersError}</p>
            ) : users.length === 0 ? (
              <p className="mt-6 text-sm text-muted-foreground">{c.noUsers}</p>
            ) : (
              <div className="mt-6 divide-y divide-border">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 py-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 overflow-hidden">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <User className="h-5 w-5" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">
                        {u.display_name || c.noName}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {u.username ? `@${u.username}` : c.noUsername}
                      </p>
                      {u.email && (
                        <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                      )}
                    </div>
                    {!u.display_name && !u.username && (
                      <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                        {c.incompleteSignup}
                      </span>
                    )}
                    {u.username && (
                      <a
                        href={`/w/${u.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-border px-3 text-xs font-semibold text-neutral-700 transition hover:border-neutral-400 hover:bg-neutral-50"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {c.wishlistLink}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        {/* === TAB: Gallery Homepage (admin only) === */}
        {activeTab === 'gallery' && isAdmin && <HomepageGalleryManager />}
      </main>
    </div>
  )
}
